/**
 * User identity management.
 * Anonymous users identified by random UUID stored in localStorage.
 */

const USER_ID_KEY = 'witw-user-id';

/**
 * Generate a UUID, with fallback for environments where crypto.randomUUID is unavailable.
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: generate a v4-like UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a unique user ID for this device.
 * Uses crypto.randomUUID() for secure, collision-resistant IDs.
 */
export function getUserId(): string {
  let userId = localStorage.getItem(USER_ID_KEY);
  if (!userId) {
    userId = generateUUID();
    localStorage.setItem(USER_ID_KEY, userId);
  }
  return userId;
}

/**
 * Check if user has an existing ID (for conditional sync on first visit).
 */
export function hasUserId(): boolean {
  return localStorage.getItem(USER_ID_KEY) !== null;
}

/**
 * Clear user ID (for testing/debugging).
 * WARNING: This will create a new user on next access.
 */
export function clearUserId(): void {
  localStorage.removeItem(USER_ID_KEY);
}
